import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Avatar, Button, Col, Row, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { CaretDownOutlined } from "@ant-design/icons";

import CustomModal from "../../components/CustomModal";

import { userActions } from "../../users/duck/userReducer";
import { projectActions } from "../duck/projectReducer";

import User from "../../models/User";

import constants from "../../utils/constants";
import {
  getAvatarColor,
  getNameInitials,
  paramsToString,
} from "../../utils/commonFunctions";

import "./SelectManagerModal.scss";
const { ROLE_IDS } = constants;

const SelectManagerModal = (props) => {
  const { visible, projectId, isProjectEditable } = props;

  const prevProps = useRef();
  const dispatch = useDispatch();

  const [boardKey, setBoardKey] = useState("");
  const [state, setState] = useState({});
  const [error, setError] = useState({});

  const projectObj = useSelector(({ project }) => project.map[projectId]);

  const { users, meta, loading } = useSelector(({ user }) => {
    const userMap = user.map;

    const boards = user.boards;
    const mBoard = boards[boardKey];

    const list = mBoard?.list || [];
    const meta = mBoard?.meta;
    const loading = mBoard?.loading;

    let users = list.map((userId) => userMap[userId]);
    users = users.filter((user) => user);

    return { users, meta, loading };
  });

  const loadMore = useCallback(
    (offset = 0, limit = 1000) => {
      if (loading) return;
      if (offset === 0 || meta?.totalCount > users?.length) {
        const params = {
          role_id: [
            ROLE_IDS.ADMIN,
            ROLE_IDS.SUPER_ADMIN,
            ROLE_IDS.EDITOR,
            ROLE_IDS.MANAGER,
          ],
          offset,
          limit,
        };

        setBoardKey(paramsToString(params));
        dispatch(userActions.onGetAllRequest(params));
      }
    },
    [dispatch, users?.length, meta?.totalCount, loading]
  );

  useEffect(() => {
    loadMore(users?.length, meta?.totalCount);
  }, [users?.length, meta?.totalCount]);

  useEffect(() => {
    if (visible) {
      if (!prevProps.current?.visible && visible) {
        const assignedManager = projectObj?.assigned_manager;
        const assignedEditor = projectObj?.assigned_editor;

        setState({
          assignedManager: assignedManager?._id || assignedManager,
          assignedEditor: assignedEditor?._id || assignedEditor,
        });
      } else if (prevProps.current?.loading && !projectObj?.loading) {
        if (!projectObj?.error) {
          props.handleModal();
        }
      }
    } else {
      setState({});
    }

    return () => {
      prevProps.current = {
        visible: props.visible,
        loading: projectObj?.loading,
      };
    };
  }, [visible, projectObj, props]);

  const handleChange = useCallback(
    (name) => (e) => {
      const value = e?.target?.value ?? e;

      setState((prevState) => ({ ...prevState, [name]: value }));
      setError({});
    },
    []
  );

  const onFilterOption = useCallback((input, option) => {
    const user = option.props.user;
    const fullName = user.fullName.toLowerCase();
    const email = user.email.toLowerCase();
    const mInput = input.toLowerCase();

    return fullName.indexOf(mInput) >= 0 || email.indexOf(mInput) >= 0;
  }, []);

  const hasError = useCallback(() => {
    const error = {};

    if (!state.assignedManager) {
      error.assignedManager = "Please select manager";
    }

    if (!state.assignedEditor) {
      error.assignedEditor = "Please select editor";
    }

    setError(error);

    return !!Object.keys(error).length;
  }, [state]);

  const onSubmit = useCallback(() => {
    if (!hasError()) {
      const payload = {
        _id: projectId,
        assigned_manager: state.assignedManager,
        assigned_editor: state.assignedEditor,
      };

      dispatch(projectActions.onUpdateOneRequest(payload));
    }
  }, [hasError, dispatch, projectId, state]);

  const renderFooter = useMemo(() => {
    return (
      <Row className="p10">
        {isProjectEditable && (
          <Button
            className="new_button mr5"
            type="primary"
            onClick={onSubmit}
            size="large"
            loading={projectObj?.loading}
          >
            Update
          </Button>
        )}
        <Button
          className="new_button"
          type="primary-outline"
          onClick={props?.handleModal}
          size="large"
        >
          Cancel
        </Button>
      </Row>
    );
  }, [props?.handleModal, isProjectEditable, onSubmit, projectObj?.loading]);

  return (
    <CustomModal
      title="Add people"
      className="select-manager-modal-container"
      visible={visible}
      footer={renderFooter}
      onCancel={props.handleModal}
    >
      <Col>
        {/* assign manager start */}
        <Col className="mb15">
          <Col className="mb5">
            <label className="label">Manager</label>
            <sup style={{ color: "red" }}>*</sup>
          </Col>

          <Col>
            <Select
              size="large"
              className="fw wpsb"
              placeholder="Select Manager"
              value={state.assignedManager}
              suffixIcon={<CaretDownOutlined className="f15" />}
              onChange={handleChange("assignedManager")}
              optionFilterProp="children"
              filterOption={onFilterOption}
              getPopupContainer={(trigger) => trigger.parentNode}
              allowClear
              showSearch
            >
              {users.map((userMap, index) => {
                const user = new User().fromMap(userMap);

                return (
                  <Select.Option key={index} value={user._id} user={user}>
                    <Avatar
                      size="small"
                      style={{ backgroundColor: getAvatarColor(user._id) }}
                    >
                      {getNameInitials(
                        user.fullName ? user.fullName : user.email
                      )}
                    </Avatar>

                    <span className="ml10">
                      {user.fullName
                        ? `${user.fullName} (${user.email})`
                        : user.email}
                    </span>
                  </Select.Option>
                );
              })}
            </Select>

            <Col className="error">{error.assignedManager}</Col>
          </Col>
        </Col>
        {/* assign manager end */}

        {/* assign editor end */}
        <Col>
          <Col className="mb5">
            <label className="label">Editor</label>
            <sup style={{ color: "red" }}>*</sup>
          </Col>

          <Col>
            <Select
              size="large"
              className="fw wpsb"
              placeholder="Select Editor"
              value={state.assignedEditor}
              suffixIcon={<CaretDownOutlined className="f15" />}
              onChange={handleChange("assignedEditor")}
              optionFilterProp="children"
              filterOption={onFilterOption}
              getPopupContainer={(trigger) => trigger.parentNode}
              allowClear
              showSearch
            >
              {users.map((userMap, index) => {
                const user = new User().fromMap(userMap);

                return (
                  <Select.Option key={index} value={user._id} user={user}>
                    <Avatar
                      size="small"
                      style={{ backgroundColor: getAvatarColor(user._id) }}
                    >
                      {getNameInitials(
                        user.fullName ? user.fullName : user.email
                      )}
                    </Avatar>

                    <span className="ml10">
                      {user.fullName
                        ? `${user.fullName} (${user.email})`
                        : user.email}
                    </span>
                  </Select.Option>
                );
              })}
            </Select>

            <Col className="error">{error.assignedEditor}</Col>
          </Col>
        </Col>
        {/* assign editor end */}
      </Col>
    </CustomModal>
  );
};

export default React.memo(SelectManagerModal);
